// =============================================================================
// CountdownBar — global fixed-bottom notification countdown bar
// =============================================================================
// PURPOSE: Shows "You {label} · sending in M:SS · [Preview] [Cancel]" after a
//          notifying portal action. Ticks 5:00→0 (300s window), hides at 0. Preview expands a
//          plain-text summary (NOT a Slack card replica — that would be two
//          sources of truth, per Decision Jun 4).
//
// MOUNT: App.tsx inside <AppLayout>, survives route changes. Do NOT mount per-
//        page — the bar must persist across navigation (the Cancel path breaks if
//        the component unmounts mid-countdown).
//
// CSS RULES (css-glass.md):
// - inline style={{ position:'fixed' }} — liquid-glass overrides position:relative
//   so Tailwind `fixed` is overridden; inline always wins.
// - High z-index (10000) — above glass paint layers and modals (modals use 9999).
// - NO transform on hover — backdrop-filter repaint rule.
// - bg-black/[0.03] tints, NOT bg-white/N (invisible on offwhite).
// - Semantic color tokens only.
// - Light mode only. No dark: variants.
// =============================================================================

import React from "react";
import type { CountdownHandle } from "../../hooks/usePortalNotifyCountdown";

interface Props {
  countdown: CountdownHandle;
  /** Current organization's name — used in preview summary */
  orgName?: string | null;
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function formatSeconds(s: number): string {
  const minutes = Math.floor(s / 60);
  const seconds = s % 60;
  return `${minutes}:${pad(seconds)}`;
}

export function CountdownBar({ countdown, orgName: _orgName }: Props): React.JSX.Element | null {
  const { active, secondsLeft, previewOpen, cancel, clear, sendNow, togglePreview } = countdown;

  if (!active) return null;

  const isUrgent = secondsLeft <= 10;

  return (
    // inline position:fixed — liquid-glass overrides Tailwind's 'fixed' class
    <div
      // zIndex max int — bar must sit above modals (9999), overlays, everything,
      // and persist on the page until Cancel/Hide/timer (never lost on modal close).
      style={{
        position: "fixed",
        bottom: "1.5rem",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 2147483600,
      }}
      role="status"
      aria-live="polite"
      aria-label={`Portal notification: sending in ${formatSeconds(secondsLeft)}`}
    >
      {/* Main bar */}
      <div
        className={[
          "flex items-center gap-3 px-4 py-2.5 rounded-2xl",
          "border shadow-lg",
          "bg-white border-black/[0.08]",
          "token-13 text-glass",
          "transition-shadow duration-300",
        ].join(" ")}
        style={{ minWidth: "340px", maxWidth: "520px" }}
      >
        {/* Countdown timer dot — pulses when urgent */}
        <span
          className={[
            "w-2 h-2 rounded-full flex-shrink-0",
            isUrgent ? "bg-ds-amber animate-pulse" : "bg-ds-accent",
          ].join(" ")}
        />

        {/* Message */}
        <span className="flex-1 min-w-0">
          <span className="text-glass-secondary">You </span>
          <span className="text-glass font-medium">{active.label}</span>
          <span className="text-glass-secondary"> · sending in </span>
          <span
            className={[
              "font-mono font-semibold tabular-nums",
              isUrgent ? "text-ds-amber" : "text-ds-accent",
            ].join(" ")}
          >
            {formatSeconds(secondsLeft)}
          </span>
        </span>

        {/* [Preview] button */}
        <button
          type="button"
          onClick={togglePreview}
          className="flex-shrink-0 token-12 px-2 py-1 rounded-lg text-glass-muted hover:text-glass hover:bg-black/[0.04] anim-tap transition-colors"
          aria-expanded={previewOpen}
          aria-label="Preview notification"
        >
          {previewOpen ? "Close" : "Preview"}
        </button>

        {/* [Send now] button — fire the Slack card immediately, skip the countdown */}
        <button
          type="button"
          onClick={() => void sendNow()}
          className="flex-shrink-0 token-12 px-2.5 py-1 rounded-lg font-medium text-ds-accent hover:bg-ds-accent/10 anim-tap transition-colors"
          aria-label="Send notification now"
        >
          Send now
        </button>

        {/* [Hide] button — dismiss the bar locally but let the server still fire */}
        <button
          type="button"
          onClick={clear}
          className="flex-shrink-0 token-12 px-2 py-1 rounded-lg text-glass-muted hover:text-glass hover:bg-black/[0.04] anim-tap transition-colors"
          aria-label="Hide bar (notification still sends)"
        >
          Hide
        </button>

        {/* [Cancel] button */}
        <button
          type="button"
          onClick={() => void cancel()}
          className="flex-shrink-0 token-12 px-2.5 py-1 rounded-lg font-medium text-ds-red hover:bg-ds-red/10 anim-tap transition-colors"
          aria-label="Cancel notification"
        >
          Cancel
        </button>
      </div>

      {/* Preview panel — plain text summary, NOT a Slack card replica */}
      {previewOpen && (
        <div
          className="mt-2 px-4 py-3 rounded-2xl border border-black/[0.08] bg-white shadow-lg token-12 text-glass-secondary"
          style={{ minWidth: "340px", maxWidth: "520px" }}
        >
          <div className="font-semibold text-glass token-13 mb-1">Preview</div>
          <div className="text-glass-secondary">
            <span className="font-medium text-glass">{active.label}</span>
            {" — "}
            Slack will receive a card for this task when the timer elapses (or immediately on page
            close). Click <span className="font-semibold text-ds-accent">Send now</span> to fire it
            instantly, or <span className="font-semibold text-ds-red">Cancel</span> to suppress it.
          </div>
        </div>
      )}
    </div>
  );
}

export default CountdownBar;
